import React, { useState, useEffect } from 'react';
import { useForm } from '@mantine/form';
import { TextInput, Button, Group, Title, createStyles, Box, Image, Text, NumberInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { saveBanner, updateBanner } from '../../services/api.service';
import { IconCheck } from '@tabler/icons-react';
import ImageDropzone from '../imageDropzone/ImageDropzone';
import { DateTimePicker } from '@mantine/dates';
import dayjs from 'dayjs';

const useStyles = createStyles((theme) => ({
    imageContainer: {
        border: '1px solid #CCCCCC',
        padding: 3,
        borderRadius: theme.radius.sm,
    }
}));


const BannerInAppForm = ({ closeModal, isEdit, banner }) => {
    const [isLoading, toggleLoading] = useState(false);
    const [fileURL, setFileURL] = useState('');
    const [isLogoChanged, toggleIsLogoChanged] = useState(false);
    const { classes } = useStyles();
   
    const form = useForm({
        initialValues: {
            title: '',
            description: '',
            startDate: null,
            endDate: null,
            cooldown: '',
            file: null,
        },
        validate: {
            title: (value) => (value ? null : 'Este campo es requerido'),
            description: (value) => (value ? null : 'Este campo es requerido'),
            startDate: (value) => (value ? null : 'Este campo es requerido'),
            endDate: (value) => (value ? null : 'Este campo es requerido'),
            cooldown: (value) => (value ? null : 'Este campo es requerido'),
            file: (value) => (!isEdit && !value ? 'La imagen es requerida' : null),
        },
    });

    useEffect(() => {
        if (isEdit) {
            form.setValues({
                title: banner.title,
                description: banner.description,
                startDate: banner.startDate ? dayjs(banner.startDate).toDate() : null,
                endDate: banner.endDate ? dayjs(banner.endDate).toDate() : null,
                cooldown: banner.cooldown,
                file: banner.file,
            });
        }
        // eslint-disable-next-line
    }, []);
    // Submit form
    const onSubmit = (values) => {
        toggleLoading(true);

        // Conditions
        if (!isEdit) {

            // Call API
            saveBanner(values, function () {
                notifications.show({
                    title: 'Banner creado correctamente',
                    color: 'green',
                    icon: <IconCheck />
                });
                setTimeout(() => {
                    closeModal();
                }, 1000);

            }, function (error) {
                toggleLoading(false);
                notifications.show({
                    title: 'Error',
                    message: error?.message || 'Error inesperado.',
                    color: 'red'
                });

            });

        } else {

            // Call API
            updateBanner(banner.id, values, function () {
                notifications.show({
                    title: 'Banner modificado correctamente',
                    color: 'green',
                    icon: <IconCheck />
                });

                setTimeout(() => {
                    closeModal();
                }, 1000);

            }, function (error) {
                toggleLoading(false);
                notifications.show({
                    title: 'Error',
                    message: 'El banner ya está activo durante el rango de fechas seleccionado',
                    color: 'red'
                });

            });
        }
    }

    const onDrop = (file) => {
        form.setFieldValue('file', file[0]);
        setFileURL(URL.createObjectURL(file[0]));
        toggleIsLogoChanged(true);
    };

    const onReject = () => {
        notifications.show({
            title: 'Error',
            message: 'Este tipo de archivo no está soportado.',
            color: 'red'
        });
    };

    return (
        <form onSubmit={form.onSubmit(onSubmit)}>
            <Title order={5} mb="md">
                {isEdit ? 'Modificar' : 'Crear'} Información del banner
            </Title>

            <Group grow sx={{ alignItems: 'baseline' }}>
                <TextInput
                    label="Título"
                    placeholder="Título del banner"
                    value={form.values.title}
                    onChange={(event) => form.setFieldValue('title', event.currentTarget.value)}
                    {...form.getInputProps('title')}
                />            
                <TextInput
                    label="Descripción"
                    placeholder="Descripción del banner"
                    value={form.values.description}
                    onChange={(event) => form.setFieldValue('description', event.currentTarget.value)}
                    {...form.getInputProps('description')}
                />
            </Group>
            <Group
                grow
                mt="md"
                sx={{ alignItems: 'baseline' }}
            >
                {/* Start Date */}
                <DateTimePicker
                    label="Fecha de Inicio"
                    placeholder="Elige una fecha"
                    valueFormat="DD-MM-YYYY HH:mm"
                    value={form.values.startDate}
                    {...form.getInputProps('startDate')}
                />
                {/* End Date */}
                <DateTimePicker
                    label="Fecha de Finalización"
                    placeholder="Elige una fecha"
                    valueFormat="DD-MM-YYYY HH:mm"
                    value={form.values.endDate}
                    minDate={form.values.startDate}
                    {...form.getInputProps('endDate')}
                />
                <NumberInput
                    label="Cooldown"
                    placeholder="Horas de visualización"
                    value={`${form.values.cooldown} horas`}
                    onChange={(event) => form.setFieldValue('cooldown', event.currentTarget.value)}
                    {...form.getInputProps('cooldown')}
                    min={0}
                    formatter={(value) => {
                        if (value === '' || value === '0') return '';
                        return `${value} ${value === '1' ? 'hora' : 'horas'}`;
                    }}
                />
            </Group>

            <Title order={5} mb="md" mt="lg">Imagen (1080x831) <span style={{ color: 'red', fontWeight: '100', lineHeight: '1.5' }}>*</span></Title>
            <Group>
                <ImageDropzone onDrop={onDrop} onReject={onReject} instruction="Sube la imagen de esta ciudad" />
                {(form.values.file || (isEdit && banner.image)) && (
                    <Box className={classes.imageContainer}>
                        <Image
                            withPlaceholder
                            src={
                                isLogoChanged && fileURL
                                    ? fileURL
                                    : isEdit && banner.image
                                        ? banner.image
                                        : undefined
                            }
                            imageProps={
                                isLogoChanged && fileURL
                                    ? { onLoad: () => URL.revokeObjectURL(fileURL) }
                                    : undefined
                            }
                            height={148}
                            width={148}
                            radius="sm"
                        />
                    </Box>
                )}
            </Group>
            {form.errors.file && (
                <Text color="red" size="xs" mt={5}>
                    {form.errors.file}
                </Text>
            )}
            <Group position="apart" grow={!isEdit}>
                <Button
                    mt="xl"
                    color="red"
                    onClick={closeModal}
                    disabled={isLoading}
                    variant="light"
                    sx={{ flexGrow: isEdit ? 2 : 1 }}
                >
                    Cancelar
                </Button>
                <Button
                    mt="xl"
                    type="submit"
                    loading={isLoading}
                    variant="filled"
                    sx={{ flexGrow: isEdit ? 2 : 1 }}
                >
                    {isEdit ? 'Guardar Cambios' : 'Crear Banner'}
                </Button>
            </Group>
        </form>
    );
};

export default BannerInAppForm;
